import { useRouter, useLocation, Link } from "@tanstack/react-router";
import { ChevronLeft, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { journeyFor, useJourneyReturn } from "@/lib/journey-return";
import { trackSafeBackHistory } from "@/lib/safe-back-history";

const LABELS: Record<string, { label: string; to: string }> = {
  "/journal": { label: "Back to Journals", to: "/journals" },
  "/journals": { label: "Back to Home", to: "/" },
  "/guided-journal": { label: "Back to Journals", to: "/journals" },
  "/dissociation-journal": { label: "Back to Journals", to: "/journals" },
  "/trigger-journal": { label: "Back to Journals", to: "/journals" },
  "/abuse-journal": { label: "Back to Journals", to: "/journals" },
  "/music-journal": { label: "Back to Journals", to: "/journals" },
  "/inner-child": { label: "Back to Journals", to: "/journals" },
  "/": { label: "Back to Home", to: "/" },
  "/grounding": { label: "Back to My Recovery Journey", to: "/" },
  "/somatic": { label: "Back to My Recovery Journey", to: "/" },
  "/sounds": { label: "Back to My Recovery Journey", to: "/" },
  "/yoga": { label: "Back to My Recovery Journey", to: "/" },
  
  "/bilateral": { label: "Back to My Recovery Journey", to: "/" },
  "/tapping": { label: "Back to My Recovery Journey", to: "/" },
  "/shadow-work": { label: "Back to Journals", to: "/journals" },
  "/safety-stabilisation": { label: "Back to Journals", to: "/journals" },
  "/know-your-patterns": { label: "Back to Journals", to: "/journals" },
  "/quiz": { label: "Back to Recovery Library", to: "/library" },
  "/explore": { label: "Back to Home", to: "/" },
  "/library": { label: "Back to Home", to: "/" },
  "/courses": { label: "Back to Recovery Library", to: "/library" },
  "/therapy-approaches": { label: "Back to Recovery Library", to: "/library" },
  "/resources": { label: "Back to Recovery Library", to: "/library" },
  "/holistic-wellbeing": { label: "Back to Recovery Library", to: "/library" },
  "/art-board": { label: "Back to Creative Reflection", to: "/creative-reflection" },
  "/settings/accessibility": { label: "Back to Profile", to: "/profile" },
  "/community": { label: "Back to Home", to: "/" },
  "/community-guidelines": { label: "Back to Community", to: "/community" },
  "/events": { label: "Back to Home", to: "/" },
  "/profile": { label: "Back to Home", to: "/" },
  "/membership": { label: "Back to Home", to: "/" },
  "/membership-benefits": { label: "Back to Home", to: "/" },
  "/dreams": { label: "Back to Journals", to: "/journals" },
  "/sleep-dreams": { label: "Back to Home", to: "/" },
  "/dream-reading": { label: "Back to Dream Journal", to: "/sleep-dreams" },
  "/dream-reflections": { label: "Back to Dream Journal", to: "/sleep-dreams" },
  "/affirmations": { label: "Back to Home", to: "/" },
  "/check-in": { label: "Back to Home", to: "/" },
  "/mood": { label: "Back to Home", to: "/" },
  "/reset": { label: "Back to Home", to: "/" },
  "/guide": { label: "Back to Home", to: "/" },
  "/admin": { label: "Back to Home", to: "/" },
  "/help": { label: "Back to Home", to: "/" },
  "/faq": { label: "Back to Home", to: "/" },
  "/support": { label: "Back to Home", to: "/" },
};

export function BackButton() {
  const router = useRouter();
  const { pathname } = useLocation();
  const ret = useJourneyReturn();

  // Track actual app history, not one pathname that reverses on every tap.
  // Reloads start a new trust boundary; unknown entries use the labelled Link.
  const backHistory = useRef<ReturnType<typeof trackSafeBackHistory> | null>(null);
  useEffect(() => {
    const tracker = trackSafeBackHistory(router.history);
    backHistory.current = tracker;
    return () => {
      tracker.unsubscribe();
      backHistory.current = null;
    };
  }, [router]);

  // Only hide on the home page — every other route gets a back affordance.
  if (pathname === "/") return null;


  // Find best match (exact or prefix)
  const entry =
    LABELS[pathname] ??
    Object.entries(LABELS)
      .filter(([k]) => k !== "/" && pathname.startsWith(k + "/"))
      .sort((a, b) => b[0].length - a[0].length)[0]?.[1] ?? {
      label: "Back",
      to: "/",
    };

  const fromJourney = ret && !journeyFor(pathname) ? ret : null;
  const label = fromJourney ? `Back to ${fromJourney.label}` : entry.label;

  // Journal pages: the labelled Back always lands on the journal hub, never
  // wherever browser history happens to point (often the homepage).
  const toHub = entry.to === "/journals";
  const handleBackClick = (e: React.MouseEvent) => {
    if (toHub && !fromJourney) return; // plain Link navigation to the hub
    handleClick(e);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (fromJourney) {
      e.preventDefault();
      if (backHistory.current?.previousHref() === fromJourney.path) {
        router.history.back();
      } else {
        router.history.replace(fromJourney.path);
      }
      return;
    }
    // Pop only when the preceding entry was observed inside this app.
    if (backHistory.current?.previousHref()) {
      e.preventDefault();
      router.history.back();
      return;
    }
    // Fresh visit: plain Link navigation to the labelled destination.
  };

  return (
    <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-2 px-4 pt-4">
      <Link
        to={entry.to}
        onClick={handleBackClick}
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-2 text-xs font-medium text-foreground/80 shadow-sm transition-colors hover:bg-accent hover:text-foreground"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        {label}
      </Link>
      <Link
        to={entry.to}
        onClick={handleClick}
        aria-label="Close this page"
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground/70 shadow-sm transition-colors hover:bg-accent hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </Link>
    </div>
  );
}
